import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Divider,
  Flex,
  Text,
  Link,
  HStack,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import Icon from "@mdi/react";
import { mdiOpenInNew } from "@mdi/js";
import { QRCodeSVG } from "qrcode.react";
import { io } from "socket.io-client";
import { nanoid } from "nanoid";
import { SOCKET_URL, MOBILE_UPLOAD_BASE_URL } from "./utils";
import UploadPreviewComponent from "./UploadPreviewComponent";
// import SendSmsUploadLinkComponent from "./SendSmsUploadLinkComponent"; // TODO: leaving this logic commented out for now in case we want to bring it back
import { ENV } from "env";
import { useSessionCtx, useAnalytics } from "utils";

/**
 * onClose
 * @callback onClose
 * @returns {void}
 */

/**
 * CaptureModalComponent
 * @name CaptureModalComponent
 * @param {boolean} isOpen - Let's component know if modal should be open or not
 * @param {onClose} onClose - Callback for when the modal should be closed (used by Chakra's useDisclosure)
 * @param {onCaptureOnMobileComplete} onComplete
 * @param {string} docTypeConfigKey - References a document upload type that can be found in the 'uploadConfigs' files.  This is so the upload page can know what ref images to show, and which keys to use when sending back upload info.
 * @returns JSX.Element
 */
const CaptureModalComponent = ({ isOpen, onClose, docTypeConfigKey, onComplete }) => {
  const { debug } = useSessionCtx();
  const [socket, setSocket] = useState(null);
  const [roomId] = useState(nanoid(32)); // generate a unique ID to use for the web socket room
  const analytics = useAnalytics();

  const [uploads, setUploads] = useState({});
  const mobileUploadUrl = `${MOBILE_UPLOAD_BASE_URL}/${roomId}?docTypeConfigKey=${docTypeConfigKey}`;

  const handleUpload = useCallback(
    (refId, data) => {
      analytics.track("CaptureOnMobile_Image_Uploaded", { roomId, refId });
      setUploads((prevUploads) => {
        return { ...prevUploads, [refId]: data };
      });
    },
    [analytics, setUploads, roomId]
  );

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (isOpen) {
      analytics.page("CaptureOnMobile", "CaptureOnMobile_Modal_Viewed", { roomId });
    }
  }, [analytics, isOpen, roomId]);

  useEffect(() => {
    if (isOpen) {
      if (!socket) {
        debug("no socket, will create");
        setSocket(io(SOCKET_URL));
      } else {
        // Join a channel
        debug("has socket, will connect to room:", roomId);
        socket.emit("join", roomId);

        // client-side
        socket.on("connect", () => {
          debug("socket on.connect", socket.id); // x8WIv7-mJelg7on_ALbx
        });

        // eslint-disable-next-line no-unused-vars
        socket.on("connect_error", (err) => {
          // the reason of the error, for example "xhr poll error"
          // console.error("[socket: connect_error] msg: ", err.message);
          // some additional description, for example the status code of the initial HTTP response
          // console.error("[socket: connect_error] description: ", err.description);
          // some additional context, for example the XMLHttpRequest object
          // console.log(err.context);
        });

        socket.on("disconnect", () => {
          debug("socket on.disconnect"); // undefined
        });

        socket.on("client-image-upload", (refId, data) => {
          debug(`socket.on "client-image-upload", refId = ${refId}`);
          handleUpload(refId, data);
        });
      }
    } else {
      setUploads({});
    }
    return () => {
      // disconnect from socket when modal is closed
      if (socket && isOpen) {
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [isOpen, socket, roomId, debug, handleUpload]);

  const handleDone = () => {
    onClose();
    if (onComplete) {
      onComplete(uploads);
    }
  };

  const handleRemoveUpload = (key) => {
    const item = uploads[key];
    if (item) {
      const u = { ...uploads };
      delete u[key];
      setUploads(u);
    }
  };

  const uploadsKeys = Object.keys(uploads);
  const uploadedImages = (
    <HStack data-testid="uploaded-image-preview-wrapper" py={4} spacing={4}>
      {uploadsKeys.length === 0 ? (
        <UploadPreviewComponent isPlaceholder={true} />
      ) : (
        uploadsKeys.map((k) => {
          const data = uploads[k];
          return (
            <UploadPreviewComponent key={k} img={data} onDelete={() => handleRemoveUpload(k)} />
          );
        })
      )}
    </HStack>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="lg" m={4}>
        <ModalHeader
          display="flex"
          background="integrator.500"
          color="white"
          justifyContent="space-between"
          alignItems="center"
          borderTopRadius={6}
        >
          <Text>Capture documents on your mobile phone</Text>
          <ModalCloseButton style={{ position: "initial", top: "initial", right: "initial" }} />
        </ModalHeader>
        <ModalBody>
          <Text>
            {/* Either scan the QR code below with the camera on your mobile device, or enter your phone
            number to receive a text message. */}
            Scan the QR code below with the camera on your mobile device. { /* TODO: this text and others on the page need to be localized */ }
          </Text>
          <Box display="flex" py={8} justifyContent="center">
            <Box padding={2} bgColor="white" borderRadius={6}>
              <QRCodeSVG data-testid="qrcode-component" data-upload-url={mobileUploadUrl} size={160} value={mobileUploadUrl} />
            </Box>
            {ENV === "sandbox" ? (
              <Tooltip label="Link-out only available on sandbox">
                <Link href={mobileUploadUrl} isExternal>
                  <IconButton
                    variant="ghost"
                    color="tertiaryText"
                    icon={<Icon path={mdiOpenInNew} size={0.75} />}
                  />
                </Link>
              </Tooltip>
            ) : null}
          </Box>
          <Flex align="center">
            <Divider />
            {/* <Text p={2} color="tertiaryText">
              or
            </Text>
            <Divider /> */}
          </Flex>
          {/* <SendSmsUploadLinkComponent isVisible={isOpen} mobileUploadUrl={mobileUploadUrl} /> */}
          <Box>
            <Text fontSize="xs" as="i" color="tertiaryText">
              Images from your mobile device will show up here:
            </Text>
          </Box>
          {uploadedImages}
        </ModalBody>

        <ModalFooter>
          <Button
            isDisabled={Object.keys(uploads).length < 1}
            colorScheme="integrator"
            onClick={handleDone}
          >
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CaptureModalComponent;
